import { useCallback, useEffect, useState } from "react";
import {
  getPushState,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSupported,
} from "@/lib/push";
import {
  getNativePushState,
  isNativePushPlatform,
  subscribeToNativePush,
  unsubscribeFromNativePush,
} from "@/lib/nativePush";

/**
 * Thin reactive wrapper around /app/frontend/src/lib/push.js.
 *
 * Returns `{ loading, state, busy, error, enable, disable, refresh }`.
 * `state` is the `{supported, permission, subscribed, endpoint}` shape
 * from getPushState() so consumers can render a finite-state UI.
 */
export default function usePushState() {
  const native = isNativePushPlatform();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    supported: native ? false : isPushSupported(),
    permission: "default",
    subscribed: false,
    endpoint: null,
    native,
    reason: null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = native ? await getNativePushState() : await getPushState();
      setState(s);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [native]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const s = native ? await subscribeToNativePush() : await subscribeToPush();
      setState(s);
      return s;
    } catch (err) {
      setError(err?.message || String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  }, [native]);

  const disable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const s = native ? await unsubscribeFromNativePush() : await unsubscribeFromPush();
      setState(s);
      return s;
    } catch (err) {
      setError(err?.message || String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  }, [native]);

  return { loading, state, busy, error, enable, disable, refresh };
}
